<template>
  <div class="vitality-hub basic-gray-section white f-h">
    <AdvancedTeamPicker
      ref="teamPicker"
      :value="newGroupTeamIds"
      :use-demo-teams-tree="demoTeamsTree"
      invisible
      ok-button
      @input="teamsSet"
    />
    <!-- eslint-disable vue/singleline-html-element-content-newline -->
    <!-- Title -->
    <h1 class="basic-blue-title">
      {{ $t('message.VITALITY_HUB') }}
      <button
        ref="refresh"
        class="add-button refresh"
        :class="{ highlight: highlightRefresh }"
        @click="refresh()"
      >
        <Icon>{{ refreshMedium }}</Icon>
        <span>{{ $t('message.REFRESH') }}</span>
      </button>
    </h1>
    <div class="f-h-below-title">
      <Tabs v-model="focusedView" :tabs="views" class="f-h">
        <!-- Dates parameters positioned next to tabs -->
        <div slot="afterTabs" class="dates">
          <div class="dates-wrapper">
            <div ref="rangeStart" class="date-edition" @click="editDate('rangeStart')">
              {{ rangeStart.format($t('fullDateFormat')) }}
            </div>
            <div class="dates-separator" />
            <div ref="rangeEnd" class="date-edition" @click="editDate('rangeEnd')">
              {{ rangeEnd.format($t('fullDateFormat')) }}
            </div>
          </div>
          <input ref="dpEdit" class="hidden-dp-field">
        </div>

        <!-- Content -->
        <div class="vh-content">

          <!-- Quick help banner -->
          <div v-if="showHelpBanner" class="vh-help-banner">
            <button class="add-button close-help" @click="hideHelpBanner">
              <Icon>{{ closeMedium }}</Icon>
              <span>{{ $t('message.VH_HIDE_QUICK_HELP') }}</span>
            </button>

            <h3>
              {{ $t('help.HELP') }}
            </h3>

            <div class="vh-help-tips">
              <div v-for="tip in bannerHelpTips" :key="tip.number" class="vh-help-tip">
                <div class="number">{{ tip.number }}</div>
                <div class="tip-text">
                  <span>{{ tip.text }}</span>
                  <Icon>{{ wideArrowTopMedium }}</Icon>
                </div>
              </div>
            </div>

            <button class="add-button more-help" @click="triggerComponentHelp">
              {{ $t('message.VH_DISPLAY_HELP') }}
            </button>
          </div>
          <button v-else class="add-button show-help-banner" @click="displayHelpBanner">
            <Icon>{{ eyeMedium }}</Icon>
            <span>{{ $t('message.VH_DISPLAY_QUICK_HELP') }}</span>
          </button>
          <!-- Groups list -->
          <TransitionGroup tag="div" name="animated-list" class="vh-transition-group">
            <GroupVitalityTile
              v-for="group in teamGroups"
              :key="group.key"
              :group="group"
              class="animated-list-item"
              @criticalityClick="onCriticalityClick"
              @teamsClick="triggerTeamsEdition(group)"
              @nameEdit="onGroupNameEdit(group, $event)"
              @deleteClick="confirmDeleteGroup(group)"
            />

            <!-- New group button -->
            <div key="fake" class="group-vitality-tile animated-list-item fake">
              <div class="main-tile">
                <div class="tile-wrapper">
                  <div class="group-name" />
                  <div class="group-vitality">
                    <div class="content" />
                    <div class="cell-title" />
                  </div>
                  <div class="group-criteria">
                    <div class="content" />
                    <div class="cell-title" />
                  </div>
                  <div class="new-group-tile h-v-centerer" @click="addGroup">
                    <span>{{ $t('message.VH_ADD_GROUP') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </TransitionGroup>
        </div>
        <!-- End of main content (including header) -->
      </Tabs>

      <!-- Category panel displaying the details of the categories -->
      <transition name="fade">
        <VHCategoryPanel
          v-if="showCriteriaPanel"
          ref="catPanel"
          @close="showCriteriaPanel = false"
        />
      </transition>
      <!-- End of the category panel -->

      <!-- Loading screen cloaking the displayed data -->
      <VHLoadingScreen @refresh="refresh" />
      <!-- End of the loading screen -->

      <!-- Welcome message that leads the user to the help walkthrough if needed -->
      <transition name="fade">
        <div
          v-if="showWelcome && !loading"
          class="dark-overlay welcome-message h-v-centerer"
          @click="hideWelcomeMessage(false)"
        >
          <div class="message" @click.stop="">
            <img :src="iaHelp" class="ia-head" alt="">
            <!-- eslint-disable-next-line vue/no-v-html -->
            <div class="text" v-html="$t('message.VH_WELCOME_TOUR')" />
            <Checkbox v-model="dontAskWelcome" :title="$t('message.DONT_ASK_AGAIN')" />
            <button class="action-btn" @click="hideWelcomeMessage(true)">
              {{ $t('message.YES') }}
            </button>
            <button class="action-btn gray" @click="hideWelcomeMessage(false)">
              {{ dontAskWelcome ? $t('message.NO') : $t('message.MAYBE_LATER') }}
            </button>
          </div>
        </div>
      </transition>
      <!-- End of the Welcome message -->
    </div>
  </div>
</template>

<script>

import $ from 'jquery';
import toastr from 'toastr';
import { isEqual } from 'lodash';
// Vuex
import { createNamespacedHelpers } from 'vuex';
// Vuex utils
import {
  REFRESH_ALL_DATA,
  FETCH_GROUPS,
  SET_GROUPS,
  CREATE_GROUP,
  EDIT_GROUP,
  DELETE_GROUP,
} from '@/store/modules/vitality/action-types';
import {
  SET_RANGE,
  SET_DATA_FINALIZING,
  SHOW_WELCOME,
} from '@/store/modules/vitality/mutation-types';
// Components
import AdvancedTeamPicker from '@/components/advanced-team-picker.vue';
import Checkbox from '@/components/checkbox.vue';
import GroupVitalityTile from '@/components/vitality-hub/group-vitality-tile.vue';
import Icon from '@/components/icon.vue';
import Tabs from '@/components/tabs.vue';
import VHCategoryPanel from '@/components/vitality-hub/category-panel.vue';
import VHLoadingScreen from '@/components/vitality-hub/loading-screen.vue';
// Mixins
import mixinDateUtils from '@/mixins/date-utils.vue';
// Utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
import IAHelpStep from '@/utils/classes/IAHelpStep';
import IAHelpStepsGroup from '@/utils/classes/IAHelpStepsGroup';
import { LSK_HIDE_VH_HIDE_HELP_BANNER } from '@/utils/constants';
import { calert, decodeHTML } from '@/utils/main';
import { getRelativeRange, vitalityThresholds } from '@/utils/vhub-utils';
import { VitalityCategory } from '@/utils/classes/VitalityCategory';
import { VitalityLoadingState } from '@/utils/classes/VitalityLoadingState';
import { closeMedium, eyeMedium, refreshMedium, wideArrowTopMedium } from '@/assets/icons';
import { mainDelay } from '@/utils/expandable';

const VLS = VitalityLoadingState;

const {
  mapActions: vhMapActions,
  mapGetters: vhMapGetters,
  mapMutations: vhMapMutations,
  mapState: vhMapState,
} = createNamespacedHelpers('vitality');

const iaHelp = require('@/assets/img/ia-help.png');

export default {
  name: 'VitalityHub',

  components: {
    AdvancedTeamPicker,
    Checkbox,
    GroupVitalityTile,
    Icon,
    VHCategoryPanel,
    VHLoadingScreen,
    Tabs,
  },

  mixins: [mixinDateUtils, mixinSocketHandler],

  data() {
    return {
      VLS,
      iaHelp,
      views: [this.$t('message.VH_TEAMS_VITALITY')/*, this.$t('message.VH_IMPROVEMENT_PROJECTS')*/],
      // Images
      closeMedium,
      eyeMedium,
      refreshMedium,
      wideArrowTopMedium,
      // Help tips
      showHelpBanner: true,
      bannerHelpTips: [
        { number: 1, text: this.$t('message.VH_TIP_SETUP_GROUPS') },
        { number: 2, text: this.$t('message.VH_TIP_VITALITY') },
        { number: 3, text: this.$t('message.VH_TIP_CRITERIA') },
        { number: 4, text: this.$t('message.VH_TIP_DETAILS') },
      ],
      // All types of data that need an update
      highlightRefresh: false,
      // Id of selected teams when creating a new group
      newGroupTeamIds: [],
      groupCurrentlyEdited: null,
      // Assistant
      showWelcome: false,
      dontAskWelcome: false,
      demoTeamsTree: false,
      queryBeforeHelp: null,
      teamIdsBeforeDemo: [],
      iaTeachText: this.$t('sensei.TEACHER_TEXT'),
      iaTeachBtns: [this.teacherBtn('MANAGEMENT_OF_PERFORMANCE')],
    };
  },

  computed: {
    /** Moment: Beginning of the period to analyze */
    rangeStart: {
      get() {
        return this.$route.query.vhStart ? this.$moment(this.$route.query.vhStart) : this.$moment().startOf('month');
      },
      set(newVal) {
        this.highlightRefresh = true;
        this.SET_RANGE({ start: this.$moment(newVal), end: this.rangeEnd });
        this.replaceDateInQuery(newVal, 'vhStart');
      },
    },
    /** Moment: End of the period to analyze */
    rangeEnd: {
      get() {
        return this.$route.query.vhEnd ? this.$moment(this.$route.query.vhEnd) : this.$moment().endOf('month');
      },
      set(newVal) {
        this.highlightRefresh = true;
        this.SET_RANGE({ start: this.rangeStart, end: this.$moment(newVal) });
        this.replaceDateInQuery(newVal, 'vhEnd');
      },
    },
    /** Number: Index of the currently focused view (teams vitality or improvement projects) */
    focusedView: {
      get() {
        return Number(this.$route.query.vhFocusedView || 0) % this.views.length;
      },
      set(view) {
        const query = Object.assign({}, this.$route.query);
        view = Number(view || 0) % this.views.length;
        query.vhFocusedView = view > 0 ? view : undefined;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Boolean: Whether the criteria panel should be displayed */
    showCriteriaPanel: {
      get() {
        return this.$route.query.vhShowCriteriaPanel === 'true';
      },
      set(doShow) {
        const query = Object.assign({}, this.$route.query);
        query.vhShowCriteriaPanel = doShow ? 'true' : undefined;
        if (!isEqual(query, this.$route.query)) {
          if (doShow) {
            // Add a loading screen
            this.SET_DATA_FINALIZING(VLS.WORKING);
            // Prevent freezing UI
            setTimeout(() => {
              this.$router.replace({ query });
              this.SET_DATA_FINALIZING(VLS.READY);
            }, 50);
          } else {
            this.$router.replace({ query });
          }
        }
      },
    },
    /** Boolean: Whether the current range is from the first day of a month til the last day */
    rangeIsAMonth() {
      const { rangeStart: start, rangeEnd: end } = this;
      return start.isSame(start.clone().startOf('month'), 'd') && end.isSame(end.clone().endOf('month'), 'd');
    },
    /** Object: range as { start: string, end: string } */
    rangeParam() {
      const { start } = getRelativeRange(this.rangeObj, -6);
      return {
        start: start.format('YYYY-MM-DD'),
        end: this.rangeEnd.format('YYYY-MM-DD'),
      };
    },
    /** Object: range as { start: string, end: string } */
    rangeObj() {
      return { start: this.rangeStart, end: this.rangeEnd };
    },
    /** Array: Tabs that user can click to show a category in the category panel */
    availableTabs() {
      return [
        { key: 'all', title: this.$t('message.VH_DISPLAY_CRITERIA_ALL') },
        ...VitalityCategory.all.map(c => ({ key: c.key, title: this.$t(`message.${c.titleTr}`) })),
      ];
    },
    // =============
    // State content
    // =============
    ...vhMapState([
      'teamGroups',
      'showWelcomeMessage',
      'usingDefaultTeamGroups',
    ]),
    ...vhMapGetters([
      'loading',
      'currentGroupsTeamIds',
    ]),
  },

  created() {
    this.SET_RANGE({ start: this.rangeStart, end: this.rangeEnd });

    this.refresh();

    // Check need for welcome message
    this.showWelcome = this.showWelcomeMessage;

    this.registerSocketHandlers();
    this.checkShowHelpBanner();
  },

  beforeDestroy() {
    $(this.$refs.dpEdit).datepicker('destroy');
  },

  methods: {
    decodeHTML,
    registerSocketHandlers() {
      this.handleSocketMessage(SocketMessages.CC_MSGS.VH_GROUPS, () => {
        this.refresh();
      });
    },
    /**
     * Refresh the data from the server
     * @param  {Array|undefined} what - Type of data to refresh. may only contain content of allDataKeys
     */
    refresh() {
      if (this.loading) {
        toastr.info(this.$t('message.VH_LOADING_WAIT'));
        return;
      }
      this.REFRESH_ALL_DATA({ includeInitialData: true });
    },
    /**
     * Trigger a datepicker update
     * @param  {String} mDateKey - 'rangeStart' or 'rangeEnd'
     */
    editDate(mDateKey) {
      const diff = this.rangeEnd.diff(this.rangeStart, 'd');
      const mDate = this.$moment(this[mDateKey]);
      const $inputEl = $(this.$refs.dpEdit);
      $inputEl.val(mDate.format('YYYY-MM-DD'));
      $inputEl.datepicker('destroy');
      $inputEl.datepicker({
        classes: 'vitality-dp',
        showButtonPanel: true,
        showOtherMonths: true,
        selectOtherMonths: true,
        nextText: this.$t('message.NEX'),
        prevText: this.$t('message.PREV'),
        currentText: this.$t('message.TODAY_SHORT'),
        closeText: this.$t('message.DONE'),
        dateFormat: 'yy-mm-dd',
        defaultDate: mDate.format('YYYY-MM-DD'),
        beforeShow: (input, inst) => {
          inst.dpDiv.addClass('vitality-dp');
        },
        onSelect: (dateText, inst) => {
          mDate.set('year', inst.selectedYear);
          mDate.set('month', inst.selectedMonth);
          mDate.set('date', inst.selectedDay);

          // Update the other one if inconsistent
          if (mDateKey === 'rangeStart' && this.rangeEnd.isBefore(mDate)) {
            this.rangeEnd = this.rangeIsAMonth
              ? mDate.clone().endOf('month')
              : mDate.clone().add(diff, 'd');
          }
          if (mDateKey === 'rangeEnd' && this.rangeStart.isAfter(mDate)) {
            this.rangeStart = this.rangeIsAMonth
              ? mDate.clone().startOf('month')
              : mDate.clone().subtract(diff, 'd');
          }
          // Trigger the update
          this[mDateKey] = mDate;
        },
      });
      $inputEl.datepicker('show');
    },
    /** User decided to hide the welcome message, check for 'dont ask again' */
    hideWelcomeMessage(showHelp) {
      this.showWelcome = false;
      this.SHOW_WELCOME(!this.dontAskWelcome);

      if (showHelp) {
        this.triggerComponentHelp();
      }
    },
    /** A criticality indication was clicked. Show criteria panel */
    onCriticalityClick(criticality, category) {
      // Set criteria panel query params according to what was clicked
      const query = Object.assign({}, this.$route.query);
      if (criticality) {
        const len = vitalityThresholds.length;
        const criticalityFilter = new Array(len).fill(true).map((v, i) => i === criticality.id);
        query.vhcpCriticalityFilter = isEqual(criticalityFilter, new Array(len).fill(true))
          ? undefined
          : criticalityFilter.map(isTrue => (isTrue ? 'true' : 'false')).join(',');
      }
      if (category) {
        const idx = this.availableTabs.findIndex(cat => cat.key === category.key);
        query.vhcpFocusedCategoryTab = idx >= 0 ? this.availableTabs[idx].key : undefined;
      }

      if (!isEqual(query, this.$route.query)) {
        this.$router.replace({ query });
      }
      this.showCriteriaPanel = true;
    },
    /** Create a new group of teams */
    addGroup() {
      if (this.usingDefaultTeamGroups) {
        this.SET_GROUPS({
          groups: this.teamGroups.map(g => ({
            key: g.key,
            name: g.name,
            teams: g.teams.map(t => ({ id: t.id })),
          }))
            .concat({
              name: this.$t('message.VH_NEW_GROUP'),
              teams: [],
            }),
        }).then(() => {
          this.triggerTeamsEdition(this.teamGroups.find(g => g.orderKey === this.teamGroups.length - 1));
          this.sendSocketUpdate();
        });
      } else {
        this.CREATE_GROUP({ name: this.$t('message.VH_NEW_GROUP') }).then(() => {
          this.triggerTeamsEdition(this.teamGroups.find(g => g.orderKey === this.teamGroups.length - 1));
        }).then(this.sendSocketUpdate);
      }
    },
    /** User edited the name of a group */
    onGroupNameEdit(group, newName) {
      if (this.usingDefaultTeamGroups) {
        this.SET_GROUPS({
          groups: this.teamGroups.map(g => ({
            key: g.key,
            name: g.key === group.key ? newName : g.name,
            teams: g.teams.map(t => ({ id: t.id })),
          })),
        }).then(this.sendSocketUpdate);
      } else {
        this.EDIT_GROUP({ id: group.id, name: newName, loadingKey: 'editingName' }).then(this.sendSocketUpdate);
      }
    },
    /** User edited the name of a group */
    triggerTeamsEdition(group) {
      this.groupCurrentlyEdited = group;
      this.newGroupTeamIds = group.teams.map(team => team.id);
      this.$refs.teamPicker.edit();
    },
    /** Teams set using AdvancedTeamPicker */
    teamsSet(teamIds) {
      this.$refs.teamPicker.endEdit();
      this.newGroupTeamIds = [];

      if (this.groupCurrentlyEdited) {
        if (this.usingDefaultTeamGroups) {
          const { key } = this.groupCurrentlyEdited;
          this.SET_GROUPS({
            groups: this.teamGroups.map(g => ({
              key: g.key,
              name: g.name,
              teams: g.key === key ? teamIds.map(id => ({ id })) : g.teams.map(t => ({ id: t.id })),
            })),
          }).then(this.sendSocketUpdate);
        } else {
          this.EDIT_GROUP({ id: this.groupCurrentlyEdited.id, teams: teamIds, loadingKey: 'editingTeams' })
            .then(this.sendSocketUpdate);
        }
      }
    },
    /** Confirm the deletion of a group */
    confirmDeleteGroup(group) {
      // If last group, ask the user to reset to default groups else, ask confirmation to delete the group
      const isLast = this.teamGroups.length === 1;
      const msg = this.$t(`message.${isLast ? 'VH_CONFIRM_RESET_GROUPS' : 'VH_CONFIRM_DELETE_GROUP'}`);
      const deleteMsg = this.$t(`message.${isLast ? 'VH_RESET_GROUPS' : 'DELETE'}`);
      calert(msg, [
        { title: deleteMsg, sClass: 'red', action: () => { this.deleteGroup(group); } },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    /** Effectively delete the group */
    deleteGroup(group) {
      if (this.usingDefaultTeamGroups) {
        this.SET_GROUPS({
          groups: this.teamGroups.filter(g => g.key !== group.key).map(g => ({
            key: g.key,
            name: g.name,
            teams: g.teams.map(t => ({ id: t.id })),
          })),
        }).then(this.sendSocketUpdate);
      } else {
        this.DELETE_GROUP({ id: group.id }).then(this.sendSocketUpdate);
      }
    },
    /** Send a socket msg to notify teams that this problem sheet can be refreshed */
    sendSocketUpdate() {
      this.sendSocketMessage(SocketMessages.CC_MSGS.VH_GROUPS);
    },
    // =======================
    // State mutations/actions
    // =======================
    ...vhMapActions({
      REFRESH_ALL_DATA,
      FETCH_GROUPS,
      SET_GROUPS,
      CREATE_GROUP,
      EDIT_GROUP,
      DELETE_GROUP,
    }),
    ...vhMapMutations({
      SET_RANGE,
      SET_DATA_FINALIZING,
      SHOW_WELCOME,
    }),
    // Help Walkthrough features
    checkShowHelpBanner() {
      this.showHelpBanner = window.localStorage.getItem(LSK_HIDE_VH_HIDE_HELP_BANNER) !== 'true';
    },
    hideHelpBanner() {
      this.showHelpBanner = false;
      window.localStorage.setItem(LSK_HIDE_VH_HIDE_HELP_BANNER, 'true');
    },
    displayHelpBanner() {
      this.showHelpBanner = true;
      window.localStorage.removeItem(LSK_HIDE_VH_HIDE_HELP_BANNER);
    },
    createHelpProps() {
      return { boxed: true };
    },
    createHelpStepsGroups() {
      // Prepare query params key to update the query between steps
      const pbCriterion = VitalityCategory.problems.criteria[0];
      const pbChartKey = `ct_${pbCriterion.scoresKey}Chart`;
      const g = this.teamGroups[0];
      const detailsQuery = `vhGroupVitalityDetailsDisplayed${g.key}`;

      this.queryBeforeHelp = Object.assign({}, this.$route.query);
      let query = Object.assign({}, this.$route.query);
      delete query[detailsQuery];
      delete query[pbChartKey];
      delete query.vhShowCriteriaPanel;
      if (!isEqual(query, this.$route.query)) {
        this.$router.replace({ query });
      }


      return [
        // Main features
        new IAHelpStepsGroup(
          this.$t('help.VH_MAIN_VITALITY_TITLE'),
          [
            new IAHelpStep(null, this.$t('help.VH_INTRO')),
            new IAHelpStep(null, this.$t('help.VH_VITALITY')),
            new IAHelpStep(['.group-vitality-tile:first-child', '.group-vitality-tile:last-child'], this.$t('help.VH_GROUPS')),
            new IAHelpStep('.group-vitality-tile.fake', this.$t('help.VH_GROUP_NEW')),
            new IAHelpStep('.group-vitality-tile .group-name', this.$t('help.VH_GROUP_EDIT'), () => {
              this.$refs.teamPicker.edit();
            }),
            new IAHelpStep('.teams-search-wrapper', this.$t('help.VH_FILTER_TEAMS_OPENED'), () => {
              this.$refs.teamPicker.endEdit();
            }),
            new IAHelpStep([this.$refs.rangeStart, this.$refs.rangeEnd], this.$t('help.VH_PERIOD')),
            new IAHelpStep(this.$refs.refresh, this.$t('help.VH_REFRESH')),
          ],
          () => {
            this.$refs.teamPicker.endEdit();
          },
        ),
        // Groups details
        new IAHelpStepsGroup(
          this.$t('help.VH_GROUPS_TITLE'),
          [
            new IAHelpStep('.group-vitality-tile', this.$t('help.VH_GROUP')),
            new IAHelpStep('.group-vitality-tile .group-vitality', this.$t('help.VH_GROUP_VITALITY')),
            new IAHelpStep('.group-vitality-tile .group-vitality .chart-icon-wrapper', this.$t('help.VH_GROUP_VITALITY_CHART')),
            new IAHelpStep('.group-vitality-tile .group-criteria', this.$t('help.VH_GROUP_CRITERIA')),
            new IAHelpStep('.group-vitality-tile .group-criteria', this.$t('help.VH_GROUP_CRITERIA_DETAILS')),
            new IAHelpStep('.group-vitality-tile .group-criteria .chart-icon-wrapper', this.$t('help.VH_GROUP_CRITERIA_CHART')),
            new IAHelpStep('.group-vitality-tile .see-more', this.$t('help.VH_GROUP_DETAILS'), () => {
              query[detailsQuery] = 'true';
              this.$router.replace({ query });
            }),
            new IAHelpStep('.group-vitality-tile > .details', this.$t('help.VH_GROUP_DETAILS_CATEGORIES')),
            new IAHelpStep('.group-vitality-tile > .details', this.$t('help.VH_GROUP_DETAILS_CATEGORIES_TELLS')),
          ],
          () => {
            delete query[detailsQuery];
            if (!isEqual(query, this.$route.query)) {
              this.$router.replace({ query });
            }
          },
        ),
        // Criteria panel
        new IAHelpStepsGroup(
          this.$t('help.VH_SPECIFIC_CRITERIA'),
          [
            new IAHelpStep('.group-vitality-tile .group-criteria', this.$t('help.VH_GROUP_CLICK_CRITERIA'), () => {
              this.onCriticalityClick({ id: 0 }, { key: 'all' });
            }),
            new IAHelpStep(null, this.$t('help.VH_CRITERIA_PANEL')),
            new IAHelpStep('.vh-category-panel .tab-titles', this.$t('help.VH_CRITERIA_PANEL_TABS')),
            new IAHelpStep('.vh-category-panel .filters', this.$t('help.VH_CRITERIA_PANEL_FILTERS')),
            new IAHelpStep('.vh-category-panel .criticality-table', this.$t('help.VH_CRITERIA_PANEL_CRIT_TABLE')),
            new IAHelpStep('.vh-category-panel .criticality-table .criticality-criterion', this.$t('help.VH_CRITERIA_PANEL_CLICK_CRIT')),
            new IAHelpStep('.vh-category-panel .filters .settings-item', this.$t('help.VH_CRITERIA_PANEL_CLOSE_FILTERS'), (e, next) => {
              query = Object.assign({}, this.$route.query);
              query.vhcpCriticalityFilter = new Array(vitalityThresholds.length).fill('true').join(',');
              this.$router.replace({ query });
              this.$refs.catPanel.$refs.filters.itemExpand();
              setTimeout(next, mainDelay);
              return true;
            }),
            new IAHelpStep('.vh-category-panel .vhcp-criterion-table', this.$t('help.VH_CRITERIA_PANEL_CRITERIA'), () => {
              query[pbChartKey] = 'true';
              this.$router.replace({ query });
            }),
            new IAHelpStep('.vh-category-panel .vhcp-criterion-table', this.$t('help.VH_CRITERIA_PANEL_CRITERIA_CHART'), () => {
              delete query[pbChartKey];
              this.$router.replace({ query });
            }),
            new IAHelpStep([
              '.vhcp-criterion-table .tv-head .tv-col.score',
              '.vhcp-criterion-table .tv-rows .tv-row:last-child .tv-col.score',
            ], this.$t('help.VH_CATEGORY_TABLE_SCORE'), () => {
              delete query.vhShowCriteriaPanel;
              this.$router.replace({ query });
            }),
            new IAHelpStep(null, this.$t('help.VH_OUTRO')),
          ],
          () => {
            query = Object.assign({}, this.$route.query);
            delete query[pbChartKey];
            delete query.vhShowCriteriaPanel;
            if (!isEqual(query, this.$route.query)) {
              this.$router.replace({ query });
            }
          },
        ),
      ];
    },
    helpHidden() {
      if (this.queryBeforeHelp && !isEqual(this.queryBeforeHelp, this.$route.query)) {
        this.$router.replace({ query: this.queryBeforeHelp });
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

// Change the currently selected date to green
.vitality-dp.ui-datepicker.ui-widget-content {
  .ui-datepicker-current-day > a {
    background: $color-green-good;
    color: #ffffff;
  }
  .ui-datepicker-buttonpane button.ui-datepicker-close {
    background: $color-green-good;
  }
}
.vitality-hub {

  .dates {
    margin-right: 0.5em;
    display: flex;
    align-items: center;
    margin-left: 1em;

    .dates-wrapper {
      display: flex;
      align-items: center;
    }
    .dates-separator {
      width: 0.25em;
      height: 0.1em;
      margin: 0px 0.2em;
      background: $color-main-text;
    }

    .date-edition {
      color: $color-light-blue-text;
      padding: 3px 5px;
      cursor: pointer;
      display: inline-block;

      &:hover {
        background: $color-hoverbtn-light-blue;
      }
    }

    .hidden-dp-field {
      visibility: hidden;
      width: 0px;
      height: 0px;
      position: absolute;
    }
  }

  // Help banner
  .add-button.show-help-banner {
    margin-top: 0.5em;
    color: $color-blue-text;

    &:hover {
      background: $color-hoverbtn-blue;
    }
  }
  .vh-help-banner {
    box-sizing: border-box;
    margin-top: 1em;
    padding: 0.3em;
    background: #e0ecf8;
    border: 0px solid #6b8eb7;
    border-width: 1px 0px 1px 0px;
    position: relative;

    h3 {
      text-align: center;
      color: $color-blue-text;
    }
    .add-button.close-help {
      position: absolute;
      top: 0.3em;
      left: 0.3em;
      color: $color-blue-text;

      &:hover {
        background: $color-hoverbtn-blue;
      }
    }
    .vh-help-tips {
      margin-top: 1.5em;
      display: flex;
      min-width: 0px;

      .vh-help-tip {
        box-sizing: border-box;
        min-width: 0px;
        flex: 0 0 25%;
        display: flex;
        position: relative;
        color: $color-blue-text;

        .number {
          position: absolute;
          top: -1em;
          left: 2em;
          width: 1.8em;
          height: 1.8em;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
          border-radius: 1em;
          border: 1px solid #6b8eb7;
          color: $color-blue-text;
          font-size: 1.1em;
          font-weight: 550;
        }
        .tip-text {
          padding: 0.3em 0.5em;
          background: #fff;
          text-align: center;
          border: 1px solid #6b8eb7;
          margin: 0px 3em;
          display: flex;
          align-items: center;
          flex-flow: column;

          span {
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 calc(100% - 1.5em);
          }
          svg {
            flex: 0 0 1.5em;
            transform: rotate(180deg);
          }
        }
      }
    }
    .add-button.more-help {
      margin: 0.5em auto 0px auto;
      color: $color-blue-text;

      &:hover {
        background: $color-hoverbtn-blue;
      }
    }
  }

  .f-h-below-title {
    position: relative;
    overflow: hidden;

    .vh-content {

      > .vh-transition-group {
        padding: 1em;

        > .group-vitality-tile:not(:first-child) {
          margin-top: 1em;
        }
      }

      .group-vitality-tile.fake {
        .tile-wrapper {
          height: 4.9em;
          position: relative;
          overflow: hidden;
        }

        .new-group-tile {
          position: absolute;
          top: 0px;
          bottom: 0px;
          left: 0px;
          right: 0px;
          background: rgba(255, 255, 255, 0.1);
          transition: all .05s;
          cursor: pointer;

          span {
            color: $color-blue-text;
            font-weight: 550;
            padding: 0.5em;
            background: rgba(255, 255, 255, 0.8);
            box-shadow: 0px 0px 70px 25px rgba(255, 255, 255, 0.8);
            transition: all .05s;
          }
          &:hover {
            background: $color-hoverbtn-light-blue;

            span {
              box-shadow: initial;
              background: transparent;
            }
          }
        }
      }

    }
  }
  h1.basic-blue-title {
    position: relative;

    .add-button.refresh {
      position: absolute;
      font-size: $font-size-main;
      right: 0.7em;
      top: calc(calc(#{$h1-height} - 1.5em) / 2);
      color: #2a5a91;
      padding: 2px 5px;

      &.highlight {
        animation: refresh-background 2s infinite linear;
      }
      &:hover {
        background: $color-hoverbtn-blue !important;
      }
    }
  }

  .welcome-message {
    .message {
      background: white;
      border-radius: 20px;
      padding: 2em 2em 1.5em 2em;
      color: #585858;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-flow: column;

      .text {
        font-weight: 600;
        line-height: 1.6em;
      }
      .ia-head {
        display: block;
        height: 3em;
        width: 3em;
        background: #ffffff;
        border-radius: 2000px;
        box-sizing: border-box;
        position: absolute;
        left: 50%;
        top: 0px;
        transform: translate(-50%, -50%);
      }
    }

    button, .checkbox {
      margin-top: 0.8em;
    }
  }
  .vh-category-panel {
    position: absolute;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 100%;
    z-index: 1;
  }
}

@media (max-width: $smartphone-landscape) {
  .vitality-hub {
    h1.basic-blue-title {
      .add-button.refresh {
        font-size: $font-size-smartphone;
        top: calc(calc(#{$sm-h1-height} - 1.5em) / 2);
      }
    }
    .vh-help-banner {
      .vh-help-tips {
        .vh-help-tip {
          .number {
            width: 1.6em;
            height: 1.6em;
            font-size: 1em;
          }
          .tip-text {
            padding: 0.2em 0.4em;
            margin: 0px 1em;
          }
        }
      }
    }
  }
}

@media (max-width: $smartphone-portrait) {
  .vitality-hub {
    h1.basic-blue-title {
      .add-button {
        span {
          display: none;
        }
        svg {
          margin-left: 0px;
        }
      }
    }
    .vh-help-banner {
      .vh-help-tips {
        flex-flow: column;

        .vh-help-tip {
          flex: 1;
          flex-flow: column;
          margin-top: 0.3em;

          .number {
            left: 0;
          }
          .tip-text {
            padding: 0.2em 0.4em;
            margin: 0px 1em;
          }
        }
      }
    }
  }
}

@keyframes refresh-background {
  0% { background: transparent }
  50% { background: #fedfaf }
  100% { background: transparent }
}
</style>
